import { AppId, FCX, getEnding, getFormattedThousand, ProductLogo, TextInput } from "@datassential/platform-ui-lib";
import FoodSection from "@pages/Food/components/Section/Section";
import { useCallback, useEffect, useMemo, useState } from "react";
import FoodTabs from "@pages/Food/components/Tabs/Tabs";
import useFoodLaunches, { useFoodLaunchesWithSearch } from "@apiHooks/useFoodLaunches";
import Grid from "./Grid";
import Summary from "./Summary";
import PermissionOverlay from "@pages/Food/components/PermissionOverlay/PermissionOverlay";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { requestTrialByFeature } from "@actions/page";
import { PermissionLevelEnum } from "@models/PermissionSection";
import cn from "classnames";
import { FoodSectionEnum } from "@pages/Food/Food";
import { AiOutlineSearch } from 'react-icons/ai';
import './SectionLaunches.scss';

enum LaunchesTabs {
    Items = 'Items',
    Summary = 'Summary',
}

const FoodSectionLaunches: FCX<{
    word: string;
    setHasData: (hasData: boolean) => void;
}> = ({
    word,
    setHasData,
}) => {
    const dispatch = useDispatch();
    const requestTrial: (f: string, hideNotification?: boolean) => any = (featureName, hideNotification) => {
        if (!hideNotification) {
            toast.info('Request has been sent');
        }
        return dispatch(requestTrialByFeature(featureName));
    };
    const [secondaryQuery, setSecondaryQuery] = useState<string>('');
    const [submittedQuery, setSubmittedQuery] = useState<string>('');

    const onSecondarySearchSubmit = useCallback(() => {
        setSubmittedQuery(secondaryQuery);
    }, [secondaryQuery]);

    useEffect(() => {
        if (secondaryQuery.trim() === '') {
            setSubmittedQuery('');
        }
    }, [secondaryQuery]);

    const {
        isLoading: isMainLoading,
        data: {
            itemsCount: mainItemsCount,
            summary: mainSummary,
            ltos: mainLtos,
        },
    } = useFoodLaunches(word);

    const {
        isLoading: isSecondaryLoading,
        data: {
            itemsCount: secondaryItemsCount,
            summary: secondarySummary,
            ltos: secondaryLtos,
        },
    } = useFoodLaunchesWithSearch(word, submittedQuery);

    const isSecondaryQueryApplied = submittedQuery.trim() !== '';

    const isLoading = isMainLoading || isSecondaryLoading;
    const ltos = useMemo(
        () => isSecondaryQueryApplied ? secondaryLtos : mainLtos,
        [isSecondaryQueryApplied, secondaryLtos, mainLtos]
    );
    const summary = useMemo(
        () => isSecondaryQueryApplied ? secondarySummary : mainSummary,
        [isSecondaryQueryApplied, secondarySummary, mainSummary]
    );
    const itemsCount = useMemo(
        () => isSecondaryQueryApplied ? secondaryItemsCount : mainItemsCount,
        [isSecondaryQueryApplied, secondaryItemsCount, mainItemsCount]
    );

    const [selectedTab, setSelectedTab] = useState<LaunchesTabs>(LaunchesTabs.Items);

    const hasFullAccess = useMemo(
        () => ltos.permissionLevel === PermissionLevelEnum.Full,
        [ltos.permissionLevel]
    );

    useEffect(() => {
        setHasData(mainItemsCount > 0);
    }, [mainItemsCount, setHasData]);

    if (isMainLoading) {
        return (
            <div className="Food__section is-loading">
                <div className="spinner spinner-blink">
                    <ProductLogo appId={AppId.Snap} type="regular"/>
                </div>
            </div>
        );
    }

    if (mainItemsCount === 0) return null;

    return (
        <div
            className="Food__section"
            id={FoodSectionEnum.Launches}
            data-testid="blockFoodSectionLaunches"
        >
            <div className={cn(
                "FoodSectionLaunches",
                !hasFullAccess && 'p-bottom'
            )}>
                <div className="FoodSectionLaunches__header">
                    <FoodSection
                        headerContent={(<>
                            <div data-testid="textFoodLaunchesTitle">
                                LAUNCHES
                            </div>
                            <FoodTabs
                                modifiers={['height-l']}
                                data={[
                                    { id: LaunchesTabs.Items, title: LaunchesTabs.Items },
                                    { id: LaunchesTabs.Summary, title: LaunchesTabs.Summary },
                                ]}
                                activeTabId={selectedTab}
                                onItemClick={(id) => setSelectedTab(id as LaunchesTabs)}
                                testId="blockFoodSectionLaunchesTabs"
                            />
                        </>)}
                    />
                </div>
                <div className="FoodSectionLaunches__sub-header">
                    <div className="FoodSectionLaunches__search-box">
                        <TextInput
                            value={secondaryQuery}
                            setValue={isSecondaryLoading ? () => undefined : setSecondaryQuery}
                            onSubmit={isSecondaryLoading ? undefined : onSecondarySearchSubmit}
                            placeholder="Add to your search..."
                            className="FoodSectionLaunches__search"
                            isDisabled={isSecondaryLoading && submittedQuery.trim() !== ''}
                            testId="inputFoodSectionLaunchesSearch"
                        />
                        <AiOutlineSearch
                            className="FoodSectionLaunches__search-icon"
                            onClick={isSecondaryLoading ? undefined : onSecondarySearchSubmit}
                            data-testid="buttonFoodSectionLaunchesSearchIcon"
                        />
                    </div>
                    {!isLoading && (
                        <div
                            style={{ textAlign: 'right' }}
                            data-testid="textFoodLaunchesSubHeader"
                        >
                            Chains featured
                            <b data-testid="textFoodLaunchesSubHeaderResultsCount"> {getFormattedThousand(itemsCount)} </b>
                            new {getEnding('item', itemsCount)} with
                            <b data-testid="textFoodLaunchesSubHeaderWord"> {word}</b>
                            {submittedQuery.trim() !== '' && (<>
                                &nbsp;and<b data-testid="textFoodLaunchesSubHeaderSearch"> {submittedQuery.toUpperCase()}</b>
                            </>)}
                            .
                        </div>
                    )}
                </div>
                <div className="FoodSectionLaunches__content">
                    {!isLoading ? (<>
                        {ltos.data.length > 0 ? (<>
                            {selectedTab === LaunchesTabs.Items && (
                                <Grid
                                    isLimitedAccess={!hasFullAccess}
                                    data={ltos.data}
                                />
                            )}
                            {selectedTab === LaunchesTabs.Summary && (
                                <Summary data={summary}/>
                            )}
                        </>) : (
                            <div
                                className="FoodSectionLaunches__placeholder"
                                data-testid="textFoodLaunchesNoResultsPlaceholder"
                            >
                                No items found.
                            </div>
                        )}
                    </>) : (
                        <div className="spinner spinner-blink">
                            <ProductLogo appId={AppId.Snap} type="regular"/>
                        </div>
                    )}
                </div>
            </div>
            {!hasFullAccess && (
                <PermissionOverlay
                    onContactUsClick={() => requestTrial('scores')}
                    contentStyleModifiers={['bottom']}
                    testId="blockFoodLaunchesPermissionOverlay"
                >
                    <div data-testid="textFoodLaunchesPermissionOverlayContentLine-0">
                        Upgrade your subscription to reveal more new items from top chains.
                    </div>
                </PermissionOverlay>
            )}
        </div>
    );
};

export default FoodSectionLaunches;